import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from './useAuth';
import { api } from '../api';
import { STATIC } from '../staticMode';

const LOCAL_KEY = 'seediq_word_times_v12';  // v12: energy-snap base — abandon older caches so prior edits don't re-push over it

// Auto-purge stale word-time caches from older builds, so old bounds can never re-mask
// the current base (saves you the DevTools cleanup).
try {
  Object.keys(localStorage).forEach((k) => {
    if (k.startsWith('seediq_word_times_') && k !== LOCAL_KEY) localStorage.removeItem(k);
  });
} catch { /* private mode */ }

// Backend-backed manual word timings (per user), keyed "sentenceId:wordIndex".
// Each value is {start, end, manual}: manual=true means hand-edited (recalc must
// not touch it); manual=false means recalc-owned (recalc may recompute it).
export function useWordTimes() {
  const { token } = useAuth();
  const [overrides, setOverrides] = useState({});
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;

  useEffect(() => {
    if (STATIC) return;          // static reader: no overrides — uses the baked JSON times
    if (!token) { setOverrides({}); return; }
    let alive = true;
    api('/wordtimes', { token }).then(server => {
      // SERVER (app.db) is the single source of truth. We do NOT merge localStorage back
      // in — that was letting stale cached edits from older builds MASK the current base
      // (and re-push themselves to the server). localStorage stays a write-through cache.
      const s = server || {};
      for (const k of Object.keys(s)) {                 // drop collapsed unit-bounds (":-1", end<=start)
        if (k.endsWith(':-1') && !(s[k] && s[k].end - s[k].start > 0.1)) delete s[k];
      }
      if (alive) {
        setOverrides(s);
        localStorage.setItem(LOCAL_KEY, JSON.stringify(s));
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [token]);

  const getTimes = useCallback((sentenceId, wordIdx, fallback) => {
    const o = overrides[`${sentenceId}:${wordIdx}`];
    return o ? { start: o.start, end: o.end } : { start: fallback.start, end: fallback.end };
  }, [overrides]);

  // Hand-edited (frozen against recalc)? An override with manual !== false.
  const isManual = useCallback((sentenceId, wordIdx) => {
    const o = overrides[`${sentenceId}:${wordIdx}`];
    return !!o && o.manual !== false;
  }, [overrides]);

  // manual OMITTED = preserve the existing freeze state. A plain time edit (drag, nudge)
  // must NOT freeze the word — freeze/unfreeze is a deliberate, segment-level action.
  // A bool sets it explicitly (the 凍結/解凍 segment buttons; recalc marks words false).
  // New words default unfrozen.
  const setTimes = useCallback((sentenceId, wordIdx, start, end, manual) => {
    const key = `${sentenceId}:${wordIdx}`;
    const prevO = overridesRef.current[key];
    const m = manual === undefined ? !!(prevO && prevO.manual !== false) : !!manual;
    setOverrides(prev => {
      const next = { ...prev, [key]: { start, end, manual: m } };
      localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
      return next;
    });
    if (token) api('/wordtimes', { method: 'PUT', token, body: { key, start, end, manual: m } }).catch(() => {});
  }, [token]);

  // Flush ALL saved overrides — local state, this-device cache, AND the backend —
  // so every segment falls back to fresh algorithm output (clears stale old-backend saves).
  const clearAll = useCallback((prefix = '') => {
    setOverrides(prev => {
      if (!prefix) { localStorage.removeItem(LOCAL_KEY); return {}; }
      const next = {};
      for (const k of Object.keys(prev)) if (!k.startsWith(prefix)) next[k] = prev[k];
      localStorage.setItem(LOCAL_KEY, JSON.stringify(next));
      return next;
    });
    if (token) api(`/wordtimes${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ''}`, { method: 'DELETE', token }).catch(() => {});
  }, [token]);

  return { getTimes, setTimes, isManual, clearAll };
}
